
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBaseMutation } from './baseMutation';

interface ProcessResponse {
  results: any[];
  hasMore: boolean;
  nextOffset: number;
  batchSize: number;
  message: string;
}

export const useProcessMutation = () => {
  const { invalidateUpdates, showSuccessToast, showErrorToast } = useBaseMutation();

  const processUpdatesMutation = useMutation({
    mutationFn: async () => {
      console.log('Starting batched update process...');
      
      let allResults: any[] = [];
      let offset = 0;
      let hasMore = true;
      let batchCount = 0;
      const maxBatches = 20; // Safety limit to prevent infinite loops
      
      try {
        while (hasMore && batchCount < maxBatches) {
          batchCount++;
          console.log(`Processing batch ${batchCount}, offset: ${offset}`);
          
          const timeoutPromise = new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Batch processing timed out')), 25000)
          );
          
          const functionPromise = supabase.functions.invoke('auto-update-excel', {
            method: 'POST',
            body: { 
              manualRun: true,
              batchSize: 3,
              offset: offset
            }
          });
          
          const result = await Promise.race([
            functionPromise,
            timeoutPromise
          ]);
          
          if (result && typeof result === 'object' && 'error' in result) {
            const response = result as { data?: ProcessResponse; error?: any };
            
            if (response.error) {
              console.error('Error from edge function:', response.error);
              throw response.error;
            }
            
            if (response.data) {
              const batchData = response.data;
              console.log(`Batch ${batchCount} completed:`, batchData.message);
              
              allResults = [...allResults, ...batchData.results];
              hasMore = batchData.hasMore;
              offset = batchData.nextOffset;
              
              // Add a small delay between batches
              if (hasMore) {
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            } else {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }
        
        console.log(`All batches completed. Total results: ${allResults.length}`);
        
        return {
          results: allResults,
          message: `Processed ${batchCount} batches with ${allResults.length} total updates`,
          batchCount
        };
        
      } catch (error) {
        console.error('Exception in batched processing:', error);
        
        if (error instanceof Error && error.message) {
          if (error.message.includes('non-2xx status code') && error.message.includes('546')) {
            throw new Error('Resource limit exceeded. Try again in a few minutes.');
          }
        }
        
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Batched update completed successfully:', data);
      
      invalidateUpdates();
      
      const successfulUpdates = data?.results?.filter(r => r.success) || [];
      const failedUpdates = data?.results?.filter(r => !r.success) || [];
      
      const totalMatches = successfulUpdates.reduce((sum, update) => sum + (update.matches_processed || 0), 0);
      
      let description = `Successfully processed ${successfulUpdates.length} updates in ${data.batchCount} batches`;
      if (totalMatches > 0) {
        description += ` with ${totalMatches} matches`;
      }
      if (failedUpdates.length > 0) {
        description += ` (${failedUpdates.length} updates failed)`;
      }
      
      const timestamp = new Date().toLocaleString();
      description += `. Completed at: ${timestamp}`;
      
      showSuccessToast("Batched Updates Processed", description);
      
      // Force multiple invalidations
      setTimeout(() => invalidateUpdates(), 500);
      setTimeout(() => invalidateUpdates(), 2000);
      setTimeout(() => invalidateUpdates(), 5000);
    },
    onError: (error) => {
      console.error('Error in batched processing:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      let userMessage = "Failed to process updates in batches";
      
      if (errorMessage.includes('Resource limit exceeded')) {
        userMessage = "Resource limits reached. The updates are being processed in smaller batches. Try again in a few minutes.";
      } else {
        userMessage += ": " + errorMessage;
      }
      
      showErrorToast("Batch Processing Error", userMessage);
    }
  });

  return { processUpdatesMutation };
};
